import { Component, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Project } from '../../models/conversation.model';

@Component({
	selector: 'app-conversation-context-menu',
	templateUrl: './conversation-context-menu.component.html',
	styleUrls: ['./conversation-context-menu.component.scss'],
})
export class ConversationContextMenuComponent implements OnChanges {
	/** Tracks the single currently-open instance so opening one closes any other. */
	private static openInstance: ConversationContextMenuComponent | null = null;

	@Input() x = 0;
	@Input() y = 0;
	@Input() visible = false;
	@Input() projects: Project[] = [];
	/** True when the targeted conversation already belongs to a project. */
	@Input() convHasProject = false;
	/** The project ID the conversation currently belongs to (if any). */
	@Input() convProjectId: string | null = null;
	/** True when the targeted conversation is currently pinned. */
	@Input() convIsPinned = false;

	@Output() closed = new EventEmitter<void>();
	@Output() rename = new EventEmitter<void>();
	/** Emits the destination projectId, or `undefined` to remove from project. */
	@Output() moveToProject = new EventEmitter<string | undefined>();
	@Output() togglePin = new EventEmitter<void>();
	// Using `deleteConv` name to avoid shadowing the built-in `delete` keyword.
	@Output() deleteConv = new EventEmitter<void>();

	showProjectSubmenu = false;
	private submenuCloseTimeout: ReturnType<typeof setTimeout> | null = null;

	get availableProjects(): Project[] {
		return this.convProjectId ? this.projects.filter((p) => p.id !== this.convProjectId) : this.projects;
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['visible']) {
			if (changes['visible'].currentValue === true) {
				// Close any other open instance before opening this one.
				if (ConversationContextMenuComponent.openInstance && ConversationContextMenuComponent.openInstance !== this) {
					ConversationContextMenuComponent.openInstance.visible = false;
					ConversationContextMenuComponent.openInstance.closed.emit();
				}
				ConversationContextMenuComponent.openInstance = this;
			} else if (ConversationContextMenuComponent.openInstance === this) {
				ConversationContextMenuComponent.openInstance = null;
			}
		}
	}

	@HostListener('document:click')
	onDocumentClick(): void {
		if (this.visible) {
			this.close();
		}
	}

	onRename(): void {
		this.rename.emit();
		this.close();
	}

	onMoveToProject(projectId: string | undefined): void {
		this.moveToProject.emit(projectId);
		this.close();
	}

	onTogglePin(): void {
		this.togglePin.emit();
		this.close();
	}

	onDelete(): void {
		this.deleteConv.emit();
		this.close();
	}

	onSubmenuParentEnter(): void {
		this.clearSubmenuTimeout();
		this.showProjectSubmenu = true;
	}

	onSubmenuParentLeave(): void {
		this.submenuCloseTimeout = setTimeout(() => {
			this.showProjectSubmenu = false;
		}, 250);
	}

	onSubmenuEnter(): void {
		this.clearSubmenuTimeout();
	}

	onSubmenuLeave(): void {
		this.submenuCloseTimeout = setTimeout(() => {
			this.showProjectSubmenu = false;
		}, 250);
	}

	private clearSubmenuTimeout(): void {
		if (this.submenuCloseTimeout) {
			clearTimeout(this.submenuCloseTimeout);
			this.submenuCloseTimeout = null;
		}
	}

	private close(): void {
		this.visible = false;
		this.showProjectSubmenu = false;
		this.clearSubmenuTimeout();
		this.closed.emit();
	}
}
